<template>
  <div class="app-container freight-page">
    <freight-editor
      v-if="editing"
      ref="editor"
      :template-id="editingId"
      @saved="saved"
      @cancel="editing = false"
    />
    <template v-else>
      <header class="freight-header">
        <div>
          <h2>运费模板</h2>
          <p>设置省市配送范围与固定运费，商品保存后建立模板关联。</p>
        </div>
        <el-button type="primary" icon="el-icon-plus" @click="edit(null)"
          >新建模板</el-button
        >
      </header>
      <div class="filter-container">
        <el-input
          v-model="query.keyword"
          clearable
          placeholder="搜索模板名称"
          label="搜索模板名称"
          @keyup.enter.native="search"
        /><el-button type="primary" icon="el-icon-search" @click="search"
          >搜索</el-button
        ><el-button @click="reset">重置</el-button>
      </div>
      <el-alert
        v-if="listError"
        :title="listError"
        type="error"
        :closable="false"
        show-icon
      />
      <el-table
        v-loading="loading"
        :data="list"
        border
        size="small"
        :empty-text="
          listError
            ? '加载失败，请重试搜索'
            : query.keyword
            ? '没有匹配的模板'
            : '暂无模板，请新建'
        "
      >
        <el-table-column label="模板名称" min-width="170"
          ><template slot-scope="s"
            ><strong>{{ s.row.name }}</strong>
            <p class="meta">
              ID {{ s.row.id }}
              <el-tag v-if="s.row.legacy" size="mini" type="info"
                >旧省级模板</el-tag
              >
            </p></template
          ></el-table-column
        >
        <el-table-column label="地区与运费规则" min-width="360"
          ><template slot-scope="s">
            <div class="rule-line">
              <el-tag type="success" size="mini">包邮</el-tag
              ><span>{{ summarize(s.row.free) }}</span>
            </div>
            <div v-for="(g, i) in s.row.groups" :key="i" class="rule-line">
              <el-tag size="mini">¥{{ g.fee }}</el-tag
              ><span>{{ summarize(g.regions) }}</span>
            </div>
            <div class="rule-line">
              <el-tag type="danger" size="mini">不配送</el-tag
              ><span>{{ summarize(s.row.blocked) }}</span>
            </div>
            <p v-if="s.row.warning" class="warning">{{ s.row.warning }}</p>
          </template></el-table-column
        >
        <el-table-column label="关联商品" width="100" align="center"
          ><template slot-scope="s"
            ><el-button type="text" @click="showGoods(s.row)"
              >{{ s.row.goodsCount }} 件</el-button
            ></template
          ></el-table-column
        >
        <el-table-column label="更新时间" width="165"
          ><template slot-scope="s">{{
            formatApiDate(s.row.updated)
          }}</template></el-table-column
        >
        <el-table-column label="操作" width="230"
          ><template slot-scope="s"
            ><el-button type="text" @click="edit(s.row.id)">编辑</el-button
            ><el-button type="text" @click="copy(s.row)">复制</el-button
            ><el-button type="text" @click="openQuote(s.row)">试算</el-button
            ><el-button
              type="text"
              class="danger-link"
              :disabled="busyId === s.row.id"
              @click="remove(s.row)"
              >删除</el-button
            ></template
          ></el-table-column
        >
      </el-table>
      <el-pagination
        class="freight-pagination"
        :current-page="query.page"
        :page-size="query.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @current-change="changePage"
        @size-change="changeSize"
      />
    </template>
    <el-dialog
      title="关联商品"
      :visible.sync="goodsVisible"
      width="min(720px, calc(100vw - 32px))"
      :close-on-click-modal="false"
    >
      <p>
        {{ goodsTemplate && goodsTemplate.name }} · 包含下架但仍绑定模板的商品
      </p>
      <el-alert
        v-if="goodsError"
        :title="goodsError"
        type="error"
        :closable="false"
      />
      <el-table v-loading="goodsLoading" :data="goods" border
        ><el-table-column
          prop="id"
          label="商品ID"
          width="110"
        /><el-table-column prop="name" label="商品名称" /><el-table-column
          prop="merchant_name"
          label="卖家"
        /><el-table-column label="状态" width="100"
          ><template slot-scope="s">{{
            ["待审核", "未上架", "已上架", "审核失败"][s.row.status] ||
              s.row.status
          }}</template></el-table-column
        ></el-table
      >
      <el-pagination
        :current-page="goodsPage"
        :page-size="15"
        :total="goodsTotal"
        layout="total, prev, pager, next"
        @current-change="loadGoods"
      />
    </el-dialog>
    <el-dialog
      title="运费试算"
      :visible.sync="quoteVisible"
      width="min(620px, calc(100vw - 32px))"
      :close-on-click-modal="false"
    >
      <p>与真实下单共用服务端规则，数量不影响固定运费。</p>
      <el-form label-position="top"
        ><el-form-item label="运费模板"
          ><el-select
            v-model="quote.id"
            filterable
            remote
            :remote-method="searchQuoteTemplates"
            :loading="quoteOptionsLoading"
            @change="calculateQuote"
            ><el-option
              v-for="t in quoteOptions"
              :key="t.id"
              :label="t.name"
              :value="t.id"/></el-select
        ></el-form-item>
        <el-form-item label="收货地区"
          ><div class="quote-address">
            <el-select
              v-model="quote.provinceId"
              filterable
              placeholder="选择省份"
              @change="provinceChanged"
              ><el-option
                v-for="p in provinces"
                :key="p.id"
                :label="p.name"
                :value="p.id"/></el-select
            ><el-select
              v-model="quote.cityId"
              filterable
              placeholder="选择城市或配送地区"
              @change="calculateQuote"
              ><el-option
                v-for="c in quoteCities"
                :key="c.id"
                :label="c.name"
                :value="c.id"
            /></el-select></div></el-form-item
      ></el-form>
      <div v-loading="quoteLoading" class="quote-result" aria-live="polite">
        <template v-if="quoteResult"
          ><strong>{{
            quoteResult.fee == null
              ? quoteResult.message
              : `¥${quoteResult.fee}`
          }}</strong>
          <p v-if="quoteResult.fee != null">
            {{ quoteResult.message }}
          </p></template
        >
        <p v-else>请选择模板、省份和具体城市后试算。</p>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import FreightEditor from "./components/FreightEditor.vue";
import {
  freightRegions,
  freightList,
  freightCopy,
  freightDelete,
  freightGoods,
  freightQuote
} from "@/api/freight";
import { compact } from "@/utils/freight-regions";
import { formatApiDate } from "@/utils/utils";
export default {
  name: "FreightTemplates",
  components: { FreightEditor },
  data() {
    return {
      query: { keyword: "", page: 1, pageSize: 15 },
      list: [],
      total: 0,
      loading: false,
      listError: "",
      provinces: [],
      editing: false,
      editingId: null,
      busyId: null,
      requestId: 0,
      goodsVisible: false,
      goods: [],
      goodsLoading: false,
      goodsError: "",
      goodsTemplate: null,
      goodsPage: 1,
      goodsRequestId: 0,
      goodsTotal: 0,
      quoteVisible: false,
      quote: { id: null, provinceId: null, cityId: null },
      quoteOptions: [],
      quoteOptionsLoading: false,
      quoteResult: null,
      quoteLoading: false,
      quoteRequestId: 0,
      optionsRequestId: 0
    };
  },
  computed: {
    quoteCities() {
      const p = this.provinces.find(p => p.id === this.quote.provinceId);
      return p ? p.children : [];
    }
  },
  async created() {
    await this.loadRegions();
    this.load();
  },
  async beforeRouteLeave(to, from, next) {
    next(!this.editing || (await this.$refs.editor.confirmLeave()));
  },
  methods: {
    formatApiDate,
    async loadRegions() {
      try {
        const r = await freightRegions();
        if (r.bizcode === 100) this.provinces = r.data;
      } catch (_) {
        /* 编辑器可独立重试地区加载。 */
      }
    },
    async load() {
      const request = ++this.requestId;
      this.loading = true;
      this.listError = "";
      try {
        const r = await freightList(this.query);
        if (request !== this.requestId) return;
        if (r.bizcode !== 100) throw new Error(r.msg);
        this.list = r.data.entitys;
        this.total = r.data.totalCount;
      } catch (e) {
        if (request === this.requestId) {
          this.list = [];
          this.total = 0;
          this.listError = e.message || "加载失败，请重试";
        }
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
    search() {
      this.query.page = 1;
      this.load();
    },
    reset() {
      this.query.keyword = "";
      this.search();
    },
    changePage(page) {
      this.query.page = page;
      this.load();
    },
    changeSize(size) {
      this.query.pageSize = size;
      this.search();
    },
    summarize(codes) {
      const labels = compact(codes, this.provinces).map(t => t.label);
      return labels.length
        ? `${labels.slice(0, 4).join("、")}${
            labels.length > 4 ? ` 等（${codes.length}个配送地区）` : ""
          }`
        : "未设置";
    },
    edit(id) {
      this.editingId = id;
      this.editing = true;
    },
    saved() {
      this.editing = false;
      this.load();
      this.loadRegions();
    },
    async copy(row) {
      try {
        const {
          value
        } = await this.$prompt(
          "复制后生成独立模板，不继承商品关联。请输入新的唯一名称：",
          "复制运费模板",
          {
            inputValue: `${row.name.slice(0, 25)}-副本`,
            inputValidator: v =>
              (!!v && !!v.trim() && Array.from(v.trim()).length <= 30) ||
              "名称必填且最多30字"
          }
        );
        const r = await freightCopy({ id: row.id, name: value.trim() });
        if (r.bizcode !== 100) return;
        this.$message.success("已复制，未关联任何商品");
        this.load();
      } catch (e) {
        if (e !== "cancel" && e !== "close")
          this.$message.error(e.message || "复制失败，请重试");
      }
    },
    async remove(row) {
      if (row.goodsCount)
        return this.$message.warning(
          "包括下架商品在内仍有关联，请先到商品管理更换模板"
        );
      try {
        await this.$confirm(`确定删除“${row.name}”？`, "删除运费模板", {
          type: "warning"
        });
      } catch (_) {
        return;
      }
      this.busyId = row.id;
      try {
        const r = await freightDelete({ id: row.id, revision: row.revision });
        if (r.bizcode !== 100) return;
        this.$message.success("已删除");
        if (this.list.length === 1 && this.query.page > 1) this.query.page--;
        this.load();
      } catch (_) {
        /* 通用请求层提示异常，列表保留供重试。 */
      } finally {
        this.busyId = null;
      }
    },
    showGoods(row) {
      this.goodsTemplate = row;
      this.goodsVisible = true;
      this.loadGoods(1);
    },
    async loadGoods(page) {
      const request = ++this.goodsRequestId;
      this.goodsPage = page;
      this.goodsLoading = true;
      this.goodsError = "";
      try {
        const r = await freightGoods({
          id: this.goodsTemplate.id,
          page,
          pageSize: 15
        });
        if (request !== this.goodsRequestId) return;
        if (r.bizcode !== 100) throw new Error(r.msg);
        this.goods = r.data.entitys;
        this.goodsTotal = r.data.totalCount;
      } catch (e) {
        if (request !== this.goodsRequestId) return;
        this.goods = [];
        this.goodsError = e.message || "加载商品失败，请重新打开重试";
      } finally {
        if (request === this.goodsRequestId) this.goodsLoading = false;
      }
    },
    async openQuote(row) {
      this.quote = { id: row.id, provinceId: null, cityId: null };
      this.quoteResult = null;
      this.quoteOptions = [row];
      this.quoteVisible = true;
      if (!this.provinces.length) await this.loadRegions();
    },
    async searchQuoteTemplates(keyword) {
      const request = ++this.optionsRequestId;
      this.quoteOptionsLoading = true;
      try {
        const r = await freightList({ keyword, page: 1, pageSize: 30 });
        if (r.bizcode === 100 && request === this.optionsRequestId)
          this.quoteOptions = r.data.entitys;
      } catch (_) {
        /* 请求层已有失败提示。 */
      } finally {
        if (request === this.optionsRequestId) this.quoteOptionsLoading = false;
      }
    },
    provinceChanged() {
      this.quote.cityId =
        this.quoteCities.length === 1 ? this.quoteCities[0].id : null;
      this.calculateQuote();
    },
    async calculateQuote() {
      const request = ++this.quoteRequestId;
      this.quoteResult = null;
      this.quoteLoading = false;
      if (!this.quote.id || !this.quote.provinceId) return;
      this.quoteLoading = true;
      try {
        const r = await freightQuote(this.quote);
        if (request !== this.quoteRequestId) return;
        if (r.bizcode !== 100) throw new Error(r.msg);
        this.quoteResult = r.data;
      } catch (e) {
        if (request === this.quoteRequestId)
          this.quoteResult = { message: e.message || "试算失败，请重试" };
      } finally {
        if (request === this.quoteRequestId) this.quoteLoading = false;
      }
    }
  }
};
</script>
<style scoped>
.freight-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}
.freight-header h2 {
  margin: 0 0 10px;
}
.freight-header p,
.meta {
  color: #606266;
  font-size: 13px;
}
.filter-container {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}
.filter-container > .el-input {
  width: 260px;
}
.filter-container > .el-button {
  margin-left: 0;
}
.rule-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 8px 0;
  line-height: 1.7;
}
.rule-line > .el-tag {
  flex: none;
}
.rule-line > span:last-child {
  overflow-wrap: anywhere;
}
.warning,
.danger-link {
  color: #c45656;
}
.freight-pagination {
  margin-top: 24px;
  text-align: right;
}
.quote-address {
  display: flex;
  gap: 12px;
}
.quote-address > .el-select {
  width: 50%;
}
.quote-result {
  background: #f5f2ff;
  padding: 24px;
  min-height: 110px;
  line-height: 1.8;
}
.quote-result strong {
  font-size: 24px;
}
.freight-page /deep/ .el-dialog {
  max-width: calc(100vw - 32px);
}
@media (max-width: 800px) {
  .freight-header {
    align-items: flex-start;
    gap: 12px;
  }
  .filter-container {
    flex-wrap: wrap;
  }
}
</style>
